<script>
  /**
   * @fileoverview AudienceInsights component provides comprehensive audience analytics
   * including recipient filtering, chart grids, and geographic mapping of email activity.
   * Used for analyzing email campaign performance and audience engagement patterns.
   *
   * @component AudienceInsights
   * @description Displays audience analytics with interactive charts and geographic mapping.
   * Includes recipient filtering to drill down into specific email addresses and analyze
   * their engagement patterns across different email activities.
   *
   * @prop {AudienceRow} row - Audience data containing activity entries and metadata
   * @prop {string|null} recipientFilter - Optional email filter for focused analysis
   * @prop {boolean} isInsights - Whether component is used in insights context
   *
   * @example
   * <AudienceInsights
   *   row={{
   *     activity: [
   *       { recipient: 'user@example.com', verb: 'sent', timestamp: '2023-01-01' },
   *       { recipient: 'user@example.com', verb: 'opened', timestamp: '2023-01-02' }
   *     ],
   *     name: 'Newsletter Campaign',
   *     id: 123
   *   }}
   *   recipientFilter="user@example.com"
   *   isInsights={true}
   * />
   */

  import { format } from 'svelte-i18n';

  import { RecipientFiltering } from '@client/components/recipient-filtering';

  import { AudienceChartGrid } from './chart-grid';
  import { AudienceMap } from './map';

  /**
   * @typedef {Object} ActivityEntry
   * @property {string} [recipient] - Email recipient address
   * @property {string} [sender] - Email sender address
   * @property {string} [verb] - Activity type (sent, opened, clicked, etc.)
   * @property {string} [objecttype] - Type of email object
   * @property {Object} [note] - Additional metadata about the activity
   * @property {string} [timestamp] - When the activity occurred
   * @property {Object} [geo] - Geographic information if available
   * @property {number} [geo.lat] - Latitude coordinate
   * @property {number} [geo.lng] - Longitude coordinate
   * @property {string} [geo.country] - Country name
   * @property {string} [geo.city] - City name
   */

  /**
   * @typedef {Object} AudienceRow
   * @property {ActivityEntry[]} [activity] - Array of email activity entries
   * @property {string} [name] - Campaign or audience name
   * @property {number} [id] - Unique identifier
   * @property {Date} [created] - Creation timestamp
   * @property {Object} [stats] - Aggregated statistics
   */

  /**
   * Audience data row containing activity entries and metadata for analysis.
   * Includes raw activity data that gets processed into charts and map markers.
   * @type {AudienceRow}
   */
  export let row = {};

  /**
   * Optional recipient email filter to narrow analysis to specific recipients.
   * When set, all charts and maps will filter data to match this recipient.
   * @type {string | null}
   */
  export let recipientFilter = null;

  /**
   * Whether the component is being used in the insights audience tab context.
   * Affects which charts are displayed and how data is aggregated.
   * @type {boolean}
   */
  export let isInsights = false;
</script>

<RecipientFiltering bind:recipient={recipientFilter}>
  {@html $format('label.AUDIENCE_INSIGHTS_RECIPIENT_FILTERING', { values: { filter: recipientFilter } })}
</RecipientFiltering>

<AudienceChartGrid bind:row bind:recipientFilter bind:isInsights />

<AudienceMap bind:row bind:recipientFilter />

<style lang="scss" src="./audience-insights.scss"></style>
